// Portfolio Administration v1.0 PR 4b §7.8 — sticky cure-period
// countdown panel. Renders only while the case status is in
// notice-of-default-sent or cure-period-active. Color band follows
// Convention 8 (see curePeriodBand.ts): green > 14d, yellow 8–14d,
// orange 3–7d, red 0–2d, grey expired.
//
// Workers drive expiration (PR 2c); this panel is read-only display
// + the three available manual actions (mark-cured, grant-extension,
// send-reminder).

import { useEffect, useMemo, useState } from "react";
import { Clock } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useAuth } from "@/hooks/useAuth";
import {
  useExtendCurePeriod,
  useMarkCurePeriodCured,
} from "@/hooks/portfolioAdmin/usePr4b";
import type {
  CurePeriodDto,
  DefaultCaseDto,
} from "@/api/portfolioAdmin/pr4bTypes";
import {
  curePeriodBand,
  curePeriodBandClass,
  curePeriodDaysRemaining,
  curePeriodLabel,
} from "./curePeriodBand";
import { useDateFormatter } from "@/utils/datetime";

const ACTIVE_STATES = new Set([
  "notice-of-default-sent",
  "cure-period-active",
]);

interface CurePeriodStickyPanelProps {
  defaultCase: DefaultCaseDto;
  curePeriod: CurePeriodDto | null;
}

type Action = "mark-cured" | "extend" | "remind" | null;

export function CurePeriodStickyPanel({
  defaultCase,
  curePeriod,
}: CurePeriodStickyPanelProps) {
  const { tz } = useDateFormatter();
  const auth = useAuth();
  const [now, setNow] = useState(() => Date.now());

  // PR 4b — client-side countdown re-render every 60s so users get a
  // live ticking display without polling the backend. Backend workers
  // remain the source of truth for actually expiring the period.
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(t);
  }, []);

  const markCured = useMarkCurePeriodCured(defaultCase.id, defaultCase.loanId);
  const extend = useExtendCurePeriod(defaultCase.id);

  const [action, setAction] = useState<Action>(null);
  const [extensionDays, setExtensionDays] = useState(30);
  const [justification, setJustification] = useState("");
  const [error, setError] = useState<string | null>(null);

  const daysRemaining = useMemo(() => {
    if (!curePeriod) return null;
    void now;
    return curePeriodDaysRemaining(
      curePeriod.endDate,
      curePeriod.extendedEndDate,
    );
  }, [curePeriod, now, tz]);

  if (!ACTIVE_STATES.has(defaultCase.status) || !curePeriod) return null;

  const band = curePeriodBand(daysRemaining);
  const endsOn = (curePeriod.extendedEndDate ?? curePeriod.endDate).slice(0, 10);

  const canMarkCured =
    auth.hasPermission("Defaults.Manage") || auth.hasRoleCode("ADMIN");
  const canExtend = auth.hasRoleCode("E2") || auth.hasRoleCode("ADMIN");
  const canRemind = canMarkCured;

  const closeModal = () => {
    if (markCured.isPending || extend.isPending) return;
    setAction(null);
    setExtensionDays(30);
    setJustification("");
    setError(null);
  };

  const handleMarkCured = async () => {
    setError(null);
    try {
      await markCured.mutateAsync({
        id: curePeriod.id,
        body: { notes: justification.trim() || undefined },
      });
      closeModal();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Mark cured failed");
    }
  };

  const handleExtend = async () => {
    setError(null);
    try {
      await extend.mutateAsync({
        id: curePeriod.id,
        additionalDays: extensionDays,
        justification: justification.trim(),
      });
      closeModal();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Extension failed");
    }
  };

  return (
    <div
      className="sticky bottom-space-4 z-30 mt-space-3"
      data-testid="cure-period-panel"
    >
      <div
        className={`flex items-center justify-between gap-space-3 flex-wrap rounded-md border-l-4 px-space-3 py-space-2 ${curePeriodBandClass(band)}`}
        role="status"
        data-testid="cure-period-band"
        data-band={band}
      >
        <div className="flex items-center gap-space-2">
          <Clock size={16} aria-hidden />
          <p className="m-0 text-small font-bold">
            Cure period: {curePeriodLabel(daysRemaining)} • ends {endsOn}
          </p>
        </div>
        <div className="flex items-center gap-space-2 flex-wrap">
          {canMarkCured && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setAction("mark-cured")}
              data-testid="cure-period-mark-cured"
            >
              Mark cured
            </Button>
          )}
          {canExtend && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setAction("extend")}
              data-testid="cure-period-extend"
            >
              Grant 30-day extension (E2)
            </Button>
          )}
          {canRemind && (
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => setAction("remind")}
              data-testid="cure-period-remind"
            >
              Send reminder notice
            </Button>
          )}
        </div>
      </div>

      <Modal
        open={action === "mark-cured"}
        onClose={closeModal}
        dismissOnBackdropClick={false}
        title="Mark cure period cured"
        description="The borrower has remedied the default. The loan returns to performing."
        footer={
          <>
            <Button variant="tertiary" size="sm" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleMarkCured}
              disabled={markCured.isPending}
            >
              {markCured.isPending ? "Saving…" : "Confirm cured"}
            </Button>
          </>
        }
      >
        <label className="block text-small font-bold mb-space-1">
          Notes (optional)
        </label>
        <textarea
          rows={3}
          className="w-full border border-border-light rounded-md p-space-2 text-small"
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
        />
        {error && (
          <p className="text-small text-status-error m-0 mt-space-2" role="alert">
            {error}
          </p>
        )}
      </Modal>

      <Modal
        open={action === "extend"}
        onClose={closeModal}
        dismissOnBackdropClick={false}
        title="Grant cure-period extension (E2)"
        description="One extension per case. Adds days to the cure-period end date."
        footer={
          <>
            <Button variant="tertiary" size="sm" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleExtend}
              disabled={
                extend.isPending ||
                extensionDays < 1 ||
                justification.trim().length < 5
              }
            >
              {extend.isPending ? "Saving…" : "Grant extension"}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-space-2">
          <label className="text-small font-bold">Additional days</label>
          <input
            type="number"
            min={1}
            max={90}
            className="h-10 px-space-2 border border-border-light rounded-md text-small"
            value={extensionDays}
            onChange={(e) => setExtensionDays(Number(e.target.value) || 0)}
          />
          <label className="text-small font-bold mt-space-2">
            Justification (≥ 5 chars, required)
          </label>
          <textarea
            rows={3}
            className="w-full border border-border-light rounded-md p-space-2 text-small"
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
          />
          {error && (
            <p className="text-small text-status-error m-0" role="alert">
              {error}
            </p>
          )}
        </div>
      </Modal>

      <Modal
        open={action === "remind"}
        onClose={closeModal}
        title="Send cure-period reminder notice"
        description="Generates a statutory reminder document for the borrower."
        footer={
          <>
            <Button variant="tertiary" size="sm" onClick={closeModal}>
              Close
            </Button>
          </>
        }
      >
        <p className="text-small m-0">
          Report Generator integration for cure-period reminder notices ships
          alongside the Report Generator v3.2 pipeline. The system will queue a
          notice for B3 / A3 to review and send to the borrower of record. A
          WorkoutEvent will be appended automatically when the notice is
          dispatched.
        </p>
      </Modal>
    </div>
  );
}
